<template>
  <tabs class="!py-5">
    <template #actions>
      <Switch
        v-model="hideDeleted"
        :label="$t('hideDeletedDrivers')"
      />
    </template>
  </tabs>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/drivers/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.drivers')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(user) => user.id ? ({ name: 'driver-details', params: { driverId: user.id } }) : ({ name: 'drivers' })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :disableFrontendSorting="true"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, reactive, ref, unref} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import {
  collOptionsProvider,
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import {format} from 'date-fns';
import type {Filter} from '@/components/utils/filter/Filter.vue';
import type {Filter as FilterType, ProviderParams} from '@/components/utils/table/hooks/provider';
import type {Address, Campaign, Company, Driver, HydraCollection, Region} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import {useQueryRef} from '@/hooks/url';
import {formatNumberForCurrency, formatTimeDurationFromSeconds} from '@/utils/helpers';
import Switch from '@utils/inputs/Switch.vue';
import {useTableFields} from '@hooks/table-configuration';

const hideDeleted = ref(true);

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const filter = useQueryRef<FilterType>('filter', {});

const totalFilter = computed(() => {
  return { 'user.deleted': hideDeleted.value ? false : undefined, ...filter.value };
});

const sortKey = useQueryRef<string | undefined>('sortKey', 'createdAt');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const { t } = useI18n();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driverList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    sortable: true,
  },
  {
    key: 'user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: (v: string) => t(`title.${v ?? 'unknown'}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.driverList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'user.lastName',
    title: t('user.lastName'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('user.region'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('user.region'),
      },
    },
  },
  {
    key: 'user.email',
    title: t('user.email'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.email'),
      },
    },
  },
  {
    key: 'user.birthday',
    title: t('user.birthday'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.birthday'),
      },
    },
  },
  {
    key: 'user.phoneNumber',
    title: t('user.phoneNumber'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.phoneNumber'),
      },
    },
  },
  {
    key: 'user.language',
    title: t('user.language'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/language', (v) => ({
          value: v,
          label: t(`language.${v}`),
        })),
        label: t('user.language'),
      },
    },
  },
  {
    key: 'user.active',
    title: t('user.active'),
    csvTranslations: {
      true: t('user.isActive.true'),
      false: t('user.isActive.false'),
    },
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (active: boolean) => t(`user.isActive.${active}`),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.active'),
        labelTrue: t('user.isActive.true'),
        labelFalse: t('user.isActive.false'),
      },
    },
  },
  {
    key: 'driverType',
    title: t('driver.driverType.DEFAULT'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverType.DEFAULT'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'driverTypeAtRegistration',
    title: t('driver.driverTypeAtRegistration'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string|null) => v ? t(`driver.driverType.${v}`) : '',
    csvTranslations: {
      'none': t('driver.driverType.none'),
      'private': t('driver.driverType.private'),
      'business': t('driver.driverType.business'),
      'mixed': t('driver.driverType.mixed'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.driverTypeAtRegistration'),
        options: enumOptionsProvider('/enums/DriverType', (v) => ({
          value: v,
          label: t(`driver.driverType.${v}`),
        })),
      },
    },
  },
  {
    key: 'profileComplete',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.profileComplete'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: false,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.profileComplete'),
      },
    },
  },
  {
    key: 'liabilityDisclaimerSigned',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.liabilityDisclaimerSigned'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.liabilityDisclaimerSigned'),
      },
    },
  },
  {
    key: 'user.emailVerifiedOn',
    title: t('user.emailVerified'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: string | null) => v ? t('yes') : t('no'),
    filter: {
      key: 'exists[user.emailVerifiedOn]',
      type: 'bool',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('user.emailVerified'),
      },
    },
  },
  {
    key: 'warningLevel',
    title: t('driver.warningLevel.DEFAULT'),
    csvTranslations: {
      none: t('driver.warningLevel.none'),
      first: t('driver.warningLevel.first'),
      second: t('driver.warningLevel.second'),
      third: t('driver.warningLevel.third'),
    },
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (level: string) => t(`driver.warningLevel.${level}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/WarningLevel', (v) => ({
          value: v,
          label: t(`driver.warningLevel.${v}`),
        })),
        label: t('driver.warningLevel.DEFAULT'),
      },
    },
  },
  {
    key: 'noticeComment',
    title: t('driver.noticeComment.DEFAULT'),
    csvTranslations: {
      null: t('driver.noticeComment.null'),
      box_or_seal_problem: t('driver.noticeComment.box_or_seal_problem'),
      spare_part_needed: t('driver.noticeComment.spare_part_needed'),
      phase_3: t('driver.noticeComment.phase_3'),
      profile_wrong: t('driver.noticeComment.profile_wrong'),
      other: t('driver.noticeComment.other'),
    },
    group: t('pages.driverList.fieldGroups.care'),
    formatter: (v?: string) => v ? t(`driver.noticeComment.${v}`) : '',
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.care'),
      options: {
        options: enumOptionsProvider('/enums/DriverNoticeComment', (v) => ({
          value: v,
          label: t(`driver.noticeComment.${v}`),
        })),
        label: t('driver.noticeComment.DEFAULT'),
      },
    },
  },
  {
    key: 'caredForAt',
    title: t('driver.caredForAt'),
    group: t('pages.driverList.fieldGroups.care'),
    sortable: true,
    visible: true,
    formatter: (v: Date | null) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.care'),
      options: {
        label: t('driver.caredForAt'),
      },
    },
  },
  {
    key: 'user.address.street',
    title: t('address.street'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.street'),
      },
    },
  },
  {
    key: 'user.address.cityName',
    title: t('address.city'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.city'),
      },
    },
  },
  {
    key: 'user.address.zip',
    title: t('address.zip'),
    group: t('pages.driverList.fieldGroups.address'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.address'),
      options: {
        label: t('address.zip'),
      },
    },
  },
  {
    key: 'iban',
    title: t('bank.iban'),
    group: t('pages.driverList.fieldGroups.bank'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.bank'),
      options: {
        label: t('bank.iban'),
      },
    },
  },
  {
    key: 'bankName',
    title: t('bank.name'),
    group: t('pages.driverList.fieldGroups.bank'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.bank'),
      options: {
        label: t('bank.name'),
      },
    },
  },
  {
    key: 'payee',
    title: t('bank.accountOwner'),
    group: t('pages.driverList.fieldGroups.bank'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.bank'),
      options: {
        label: t('bank.accountOwner'),
      },
    },
  },
  {
    key: 'box.status',
    csvTranslations: {
      null: t('box.status.noBox'),
      noBox: t('box.status.noBox'),
      Init: t('box.status.Init'),
      Mounted: t('box.status.Mounted'),
      Destroyed: t('box.status.Destroyed'),
      Other: t('box.status.Other'),
      Lost: t('box.status.Lost'),
      Returned: t('box.status.Returned'),
    },
    title: t('box.status.DEFAULT'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    formatter: (v: string) => v ? t(`box.status.${v}`) : t('box.status.noBox'),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxStatus', (item: string) => ({
          label: t(`box.status.${item}`),
          value: item,
        })), [
          { label: t('box.status.noBox'), value: 'noBox' }
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['box.status'];
            m['exists[box]'] = false;
          } else {
            delete m['exists[box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.status.DEFAULT'),
      },
    },
  },
  {
    key: 'box.type',
    title: t('box.type'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: enumOptionsProvider('/enums/BoxType', (item: string) => ({
          label: t(`box.types.${item}`),
          value: item,
        })),
        label: t('box.type'),
      },
    },
  },
  {
    key: 'box.variant',
    csvTranslations: {
      unknown: t('box.variant.unknown'),
      with_base: t('box.variant.with_base'),
      without_base: t('box.variant.without_base'),
    },
    title: t('box.variant.DEFAULT'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    formatter: (v: string) => (v ? t(`box.variant.${v}`) : ''),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: enumOptionsProvider('/enums/BoxVariant', (item: string) => ({
          label: t(`box.variant.${item}`),
          value: item,
        })),
        label: t('box.variant.DEFAULT'),
      },
    },
  },
  {
    key: 'box.keyNumber',
    title: `${t('box.keyNumber')}`,
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: `${t('box.keyNumber')}`,
      },
    },
  },
  {
    key: 'box.stable',
    title: `${t('box.stable')}`,
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: boolean|null) => v ? t('yes') : (v === false ? t('no') : t('unknown')),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('unknown'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['box.stable'];
            m['exists[box.stable]'] = false;
          } else {
            delete m['exists[box.stable]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[box.stable]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: `${t('box.stable')}`,
      },
    },
  },
  {
    key: 'latestMountingTimeslotRegistration.timeslot.startTime',
    title: t('box.mountingAppointment'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.mountingAppointment'),
      },
    },
  },
  {
    key: 'box.mountingDate',
    title: t('box.mountingDate'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.mountingDate'),
      },
    },
  },
  {
    key: 'box.firstCampaign.group.name',
    title: t('box.campaign'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.campaign'),
      },
    },
  },
  {
    key: 'box.sealNumber',
    title: t('box.seal'),
    group: t('pages.driverList.fieldGroups.box'),
    sortable: true,
    filter: {
      type: 'text',
      group: t('pages.driverList.filterGroups.box'),
      options: {
        label: t('box.seal'),
      },
    },
  },
  {
    key: 'specialDriverPhoto',
    title: t('driver.specialPhoto'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.specialPhoto'),
      },
    },
  },
  {
    key: 'specialDriverStanding',
    title: t('driver.specialStanding'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.specialStanding'),
      },
    }
  },
  {
    key: 'helperDriver',
    title: t('driver.helper'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.special'),
      options: {
        label: t('driver.helper'),
      },
    },
  },
  {
    key: 'defaultSourceAddress',
    title: `${t('driver.mostFrequentPath')} ${t('from')}`,
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['defaultSourceAddress.street', 'defaultSourceAddress.cityName'],
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'defaultSourceAddress.street',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('from')}`,
              },
            },
            {
              key: 'defaultSourceAddress.zip',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'defaultSourceAddress.cityName',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'defaultDestinationAddress',
    title: `${t('driver.mostFrequentPath')} ${t('to')}`,
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: true,
    sortKey: ['defaultDestinationAddress.street', 'defaultDestinationAddress.cityName'],
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && addr.cityName &&`${addr.street}, ${addr.zip}, ${addr.cityName}`) ||
      addr?.street ||
      addr?.zip ||
      addr?.cityName,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.driverList.fieldGroups.frequency'),
        class: '!w-200',
        options: {
          filters: [
            {
              key: 'defaultDestinationAddress.street',
              type: 'text',
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('to')}`,
              },
            },
            {
              key: 'defaultDestinationAddress.zip',
              type: 'text',
              group: t('pages.driverList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.zip'),
              },
            },
            {
              key: 'defaultDestinationAddress.cityName',
              type: 'text',
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.driverList.fieldGroups.frequency'),
    sortable: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.frequency'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('createdAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('createdAt'),
      },
    },
  },
  {
    key: 'updatedAt',
    title: t('updatedAt'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('updatedAt'),
      },
    },
  },
  {
    key: 'amountOfCampaignRegistrations',
    title: t('driver.totalRegisteredCampaigns'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalRegisteredCampaigns'),
      },
    },
  },
  {
    key: 'timestampLastRide',
    title: t('driver.lastRide'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.lastRide'),
      },
    },
  },
  {
    key: 'endDateLastFinishedCampaign',
    title: t('driver.endDateLastFinishedCampaign'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.endDateLastFinishedCampaign'),
      },
    },
  },
  {
    key: 'endDateLastRegisteredCampaign',
    title: t('driver.endDateLastRegisteredCampaign'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.endDateLastRegisteredCampaign'),
      },
    },
  },
  {
    key: 'totalDrivenDistance',
    title: t('driver.totalDistance'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    unit: 'km',
    formatter: (v: number) => (v / 1000).toFixed(2) + ' km',
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>km</span>,
        label: t('driver.totalDistance'),
        transformOut: (v: number) => v * 1000,
        transformIn: (v: number) => v / 1000,
      },
    },
  },
  {
    key: 'kcalSpent',
    title: t('driver.kcal'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    unit: 'kcal',
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>kcal</span>,
        label: t('driver.kcal'),
      },
    },
  },
  {
    key: 'co2Saved',
    title: t('driver.co2'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    unit: 'kg',
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>co2</span>,
        label: t('driver.co2'),
      },
    },
  },
  {
    key: 'totalDrivenTime',
    title: t('driver.totalTime') + ' (HH:MM:SS)',
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    formatter: (v: number) => `${formatTimeDurationFromSeconds(v)}`,
    filter: {
      type: 'duration',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalTime'),
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
      },
    },
  },
  {
    key: 'avgSpeed',
    title: t('driver.averageSpeed'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    unit: 'km/h',
    formatter: (v: number) => v ? `${(v*3.6).toFixed(2)} km/h` : '0 km/h',
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        icon: <span>km/h</span>,
        label: t('driver.averageSpeed'),
        transformOut: (v: number) => v/3.6,
        transformIn: (v: number) => v*3.6,
      },
    },
  },
  {
    key: 'numOfTours',
    title: t('driver.numOfTours'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfTours'),
      },
    },
  },
  {
    key: 'numOfDaysWithTour',
    title: t('driver.numOfDaysWithTour'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfDaysWithTour'),
      },
    },
  },
  {
    key: 'numOfTopRatedPhotos',
    title: t('driver.numOfTopRatedPhotos'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.numOfTopRatedPhotos'),
      },
    },
  },
  {
    key: 'totalPoints',
    title: t('driver.totalPoints'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.totalPoints'),
      },
    },
  },
  {
    key: 'pointsLastWeek',
    title: t('driver.pointsLastWeek'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.pointsLastWeek'),
      },
    },
  },
  {
    key: 'rankLastWeek',
    title: t('driver.currentRank'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.currentRank'),
      },
    },
  },
  {
    key: 'rankStatus',
    title: t('driver.statusEarned'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: true,
    visible: true,
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        options: enumOptionsProvider('/enums/rankStatus'),
        label: t('driver.statusEarned'),
      },
    },
  },
  {
    key: 'requestedLoyaltyPointReward',
    title: t('driver.requestedLoyaltyPointReward'),
    group: t('pages.driverList.fieldGroups.loyalty'),
    sortable: true,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.loyalty'),
      options: {
        label: t('driver.requestedLoyaltyPointReward'),
      },
    },
  },
  {
    key: 'acceptedAgbVersions.privacy',
    title: t('driver.acceptedAgbVersions.privacy'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: item => item ?? 0,
    sortable: false,
    visible: false,
  },
  {
    key: 'acceptedAgbVersions.registration',
    title: t('driver.acceptedAgbVersions.registration'),
    group: t('pages.driverList.fieldGroups.general'),
    formatter: item => item ?? 0,
    sortable: false,
    visible: false,
  },
  {
    key: 'totalPaidOutPayout',
    title: t('driver.totalPaidOutPayout'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    formatter: (v: number) => v ? formatNumberForCurrency(v) : '',
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.totalPaidOutPayout'),
        icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'verificationRequested',
    title: t('driver.verificationRequested'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.verificationRequested'),
      },
    },
  },
  {
    key: 'lastVerification',
    title: t('driver.lastVerification'),
    group: t('pages.driverList.fieldGroups.calculations'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : ''),
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.calculations'),
      options: {
        label: t('driver.lastVerification'),
      },
    },
  },
  {
    key: 'openCredits',
    title: t('driver.openCredits'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: item => item === null ? '-' : formatNumberForCurrency(item),
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.openCredits'),
        icon: <span>CHF</span>,
      },
    },
  },
  {
    key: 'openDebts',
    title: t('driver.openDebts'),
    group: t('pages.driverList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: item => item === null ? '-' : formatNumberForCurrency(item),
    filter: {
      type: 'range',
      group: t('pages.driverList.filterGroups.general'),
      options: {
        label: t('driver.openDebts'),
        icon: <span>CHF</span>,
      },
    },
  },
  /**
   * WB For Business
   */
  {
    key: 'licenseSeat',
    title: t('pages.driverList.fields.licenseExists'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: false,
    visible: true,
    formatter: (v: string | null) => v ? t('yes') : t('no'),
    filter: {
      key: 'exists[licenseSeat]',
      type: 'bool',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        label: t('pages.driverList.fields.licenseExists'),
      },
    },
  },
  {
    key: 'companyProfileComplete',
    title: t('driver.companyProfileComplete') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string | null) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        label: t('driver.companyProfileComplete') + ' - For Business',
      },
    },
  },
  {
    key: 'licenseSeat.type',
    title: t('pages.driverList.fields.licenseType') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? t(`licenses.boxType.${v}`) : '-',
    csvTranslations: {
      box: t('licenses.boxType.box'),
      noBox: t('licenses.boxType.noBox'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: enumOptionsProvider('/enums/LicenseType', (v) => ({
          value: v,
          label: t(`licenses.boxType.${v}`),
        })),
        label: t('pages.driverList.fields.licenseType') + ' - For Business',
      },
    },
  },
  {
    key: 'licenseSeat.license.company.name',
    title: t('licenses.company') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    to: (driver: Driver) =>
      (driver?.licenseSeat?.license?.company?.id
        ? {
          name: 'licenses-company-details',
          params: { companyId: driver?.licenseSeat?.license?.company?.id },
        }
        : undefined),
    filter: {
      key: 'licenseSeat.license.company',
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        label: t('licenses.company') + ' - For Business',
        searchable: true,
        minChars: 2,
        filterResults: false,
        clearOnSelect: true,
        delay: 0,
        resolveOnLoad: true,
        extraOption: {
          label: t('verification.licenseSeat.no'),
          value: 'null',
        },
        options: (query: string, select: any) => {
          const input = query || select.modelValue;

          if (!input) return Promise.resolve([]);
          const filterQuery: ProviderParams = {
            filter: query ? { name: input } : { id: select.modelValue },
          };
          return collOptionsProvider<Company>('/companies', filterQuery, (value) => ({
            label: value.name,
            value: value.id,
          }))();
        },
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['licenseSeat.license.company'];
            m['exists[licenseSeat]'] = false;
          } else {
            delete m['exists[licenseSeat]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[licenseSeat]' in m) {
            return 'null';
          }
          return m[key];
        },
      },
    },
  },
  {
    key: 'saddlebags',
    title: t('driver.saddlebags'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['saddlebags'];
            m['exists[saddlebags]'] = false;
          } else {
            delete m['exists[saddlebags]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[saddlebags]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.saddlebags'),
      },
    },
  },
  {
    key: 'luggageRackInBack',
    title: t('driver.luggageRackInBack'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['luggageRackInBack'];
            m['exists[luggageRackInBack]'] = false;
          } else {
            delete m['exists[luggageRackInBack]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[luggageRackInBack]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.luggageRackInBack'),
      },
    },
  },
  {
    key: 'luggageRackUsable',
    title: t('driver.luggageRackUsable'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['luggageRackUsable'];
            m['exists[luggageRackUsable]'] = false;
          } else {
            delete m['exists[luggageRackUsable]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[luggageRackUsable]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.luggageRackUsable'),
      },
    },
  },
  {
    key: 'batteryPosition',
    title: t('driver.batteryPosition.DEFAULT'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? t(`driver.batteryPosition.${v}`) : '-',
    csvTranslations: {
      belowLuggageRack: t('driver.batteryPosition.belowLuggageRack'),
      inFrame: t('driver.batteryPosition.inFrame'),
      other: t('driver.batteryPosition.other'),
    },
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: enumOptionsProvider('/enums/BatteryPosition', (v) => ({
          value: v,
          label: v ? t(`driver.batteryPosition.${v}`) : '-',
        })),
        label: t('driver.batteryPosition.DEFAULT'),
      },
    },
  },
  {
    key: 'canton',
    title: t('driver.canton'),
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? t(`canton.${v}`) : '-',
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: enumOptionsProvider('/enums/Canton', (v) => ({
          value: v,
          label: v ? t(`canton.${v}`) : '-',
        })),
        label: t('driver.canton'),
      },
    },
  },
  {
    key: 'anonymousRanking',
    title: t('driver.anonymousRanking') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v?: boolean|null) => yesNoNull(v),
    filter: {
      type: 'dropdown',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        options: [
          {
            value: 'true',
            label: t('yes'),
          },
          {
            value: 'false',
            label: t('no'),
          },
          {
            value: 'null',
            label: t('null'),
          }
        ],
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'null') {
            delete m['anonymousRanking'];
            m['exists[anonymousRanking]'] = false;
          } else {
            delete m['exists[anonymousRanking]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[anonymousRanking]' in m) {
            return 'null';
          }
          return m[key];
        },
        label: t('driver.anonymousRanking') + ' - For Business',
      },
    },
  },
  {
    key: 'licenseSeat.license.startDate',
    title: t('licenses.startDate') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        label: t('licenses.startDate') + ' - For Business',
      },
    },
  },
  {
    key: 'licenseSeat.license.endDate',
    title: t('licenses.endDate') + ' - For Business',
    group: t('pages.driverList.fieldGroups.forBusiness'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '',
    filter: {
      type: 'datetime',
      group: t('pages.driverList.filterGroups.forBusiness'),
      options: {
        label: t('licenses.endDate') + ' - For Business',
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const { data: items, loading } = useProvider<HydraCollection<Driver[]>>('/drivers', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const yesNoNull = (v?: boolean|null) => {
  if (v === undefined || v === null) return '';
  return v ? t('yes') : t('no');
};
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
